import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isNativeApp } from '../utils/platform';
import { getClubBySlug } from '../services/clubService';
import { detectInputType, normalizePhone, formatPhoneDisplay } from '../utils/auth';
import { isNorthAmericaLocale } from '../utils/regionDefaults';

// Obfuscated demo account detection — hashes avoid exposing demo emails in the bundle
function _h(s) {
  let x = 5381;
  for (let i = 0; i < s.length; i++) x = ((x << 5) + x) ^ s.charCodeAt(i);
  return (x >>> 0).toString(36);
}
const _DH = new Set(['1kkwi4d', '1sxm2zc', 'lzveiu']);

export default function Login({ initialEmail = '', joinClubSlug = '' }) {
  // Phone-OTP is currently US/CA only — Vonage international delivery
  // hasn't been validated for non-NA carriers. Computed once at mount;
  // the user's locale doesn't change mid-session.
  const phoneAuthEnabled = isNorthAmericaLocale();
  const [identifier, setIdentifier] = useState(initialEmail);
  const [inputType, setInputType] = useState(initialEmail ? 'email' : null); // 'email' | 'phone' | null
  const [normalizedPhone, setNormalizedPhone] = useState('');
  const [joinClub, setJoinClub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isDemoLogin, setIsDemoLogin] = useState(false);
  const [demoPin, setDemoPin] = useState('');
  const [demoPinError, setDemoPinError] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const {
    signIn, signInWithPhone, signInWithGoogle, signInWithFacebook, signInWithApple,
    verifyOtp, verifyPhoneOtp, enterDemoMode,
  } = useAuth();

  // Fetch club info for join flow
  useEffect(() => {
    if (!joinClubSlug) return;
    getClubBySlug(joinClubSlug).then((club) => {
      if (club) setJoinClub(club);
    });
  }, [joinClubSlug]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const trimmed = identifier.trim();

    // Demo mode intercept — hash comparison to keep demo emails out of the bundle
    if (_DH.has(_h(trimmed.toLowerCase()))) {
      setIsDemoLogin(true);
      setLoading(false);
      return;
    }

    const type = detectInputType(trimmed);
    if (!type) {
      setError('Please enter a valid email or phone number.');
      setLoading(false);
      return;
    }

    setInputType(type);

    // Region gate — phone OTP is US/CA only for now. The locale is only a
    // hint (a US iPhone set to English (UK) reports en-GB), so a number that
    // is clearly North American is always allowed through.
    const phoneDigits = trimmed.replace(/\D/g, '');
    const looksNorthAmerican = trimmed.startsWith('+')
      ? phoneDigits.startsWith('1') && phoneDigits.length === 11
      : (phoneDigits.length === 10 && !/^[01]/.test(phoneDigits))
        || (phoneDigits.length === 11 && phoneDigits.startsWith('1'));
    if (type === 'phone' && !phoneAuthEnabled && !looksNorthAmerican) {
      setError('Phone sign-in is currently available in the US and Canada only. Please sign in with your email address.');
      setLoading(false);
      return;
    }

    try {
      if (type === 'phone') {
        const normalized = normalizePhone(trimmed, looksNorthAmerican ? '1' : undefined);
        setNormalizedPhone(normalized);
        await signInWithPhone(normalized);
      } else {
        await signIn(trimmed);
      }
      setSent(true);
      setResendCooldown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err.message);
      setGoogleLoading(false);
    }
  };

  const handleFacebookSignIn = async () => {
    setFacebookLoading(true);
    setError('');
    try {
      await signInWithFacebook();
    } catch (err) {
      setError(err.message);
      setFacebookLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setAppleLoading(true);
    setError('');
    try {
      await signInWithApple();
    } catch (err) {
      setError(err.message);
      setAppleLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError('');

    try {
      if (inputType === 'phone') {
        await verifyPhoneOtp(normalizedPhone, otpCode);
      } else {
        await verifyOtp(identifier.trim(), otpCode);
      }
      // Session is automatically picked up by onAuthStateChange in AuthContext
    } catch (err) {
      if (err.message?.includes('expired')) {
        setError('That code has expired. Please request a new one.');
      } else if (err.message?.includes('invalid') || err.message?.includes('Token')) {
        setError('Invalid code. Please check and try again.');
      } else {
        setError(err.message);
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setLoading(true);

    try {
      if (inputType === 'phone') {
        await signInWithPhone(normalizedPhone);
      } else {
        await signIn(identifier.trim());
      }
      setResendCooldown(60);
      setOtpCode('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoPin = async (e) => {
    e.preventDefault();
    setDemoPinError('');
    setDemoLoading(true);
    try {
      await enterDemoMode(identifier.trim().toLowerCase(), demoPin);
    } catch {
      setDemoPinError('Demo login failed. Please try again.');
      setDemoPin('');
    } finally {
      setDemoLoading(false);
    }
  };

  // Display value for the OTP screen
  const sentToDisplay = inputType === 'phone'
    ? formatPhoneDisplay(normalizedPhone)
    : identifier.trim();

  // Demo mode: show PIN entry screen
  if (isDemoLogin) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: '3.5rem', marginBottom: 16, textAlign: 'center' }}>🔐</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, textAlign: 'center' }}>
            Enter Demo PIN
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', textAlign: 'center', marginBottom: 20, lineHeight: 1.5 }}>
            Enter the 4-digit PIN to access demo mode
          </p>

          {demoPinError && (
            <div style={{
              background: 'rgba(211, 71, 74, 0.1)',
              border: '1px solid rgba(211, 71, 74, 0.3)',
              borderRadius: 'var(--radius-sm)',
              padding: '10px 14px',
              marginBottom: 16,
              fontSize: '0.8125rem',
              color: 'var(--accent)',
            }}>
              {demoPinError}
            </div>
          )}

          <form onSubmit={handleDemoPin}>
            <div style={{ marginBottom: 16 }}>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={demoPin}
                onChange={(e) => setDemoPin(e.target.value.replace(/\D/g, ''))}
                placeholder="0000"
                required
                autoFocus
                className="form-input"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  textAlign: 'center',
                  letterSpacing: '0.3em',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <button
              type="submit"
              disabled={demoPin.length !== 4 || demoLoading}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '0.9375rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: 'var(--accent)',
                color: '#fff',
                cursor: (demoPin.length !== 4 || demoLoading) ? 'not-allowed' : 'pointer',
                opacity: (demoPin.length !== 4 || demoLoading) ? 0.6 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {demoLoading ? 'Signing in...' : 'Enter Demo'}
            </button>
          </form>

          <button
            onClick={() => {
              setIsDemoLogin(false);
              setDemoPin('');
              setDemoPinError('');
              setIdentifier('');
            }}
            style={{ ...buttonSecondaryStyle, marginTop: 16 }}
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  // After sending code: show OTP code entry form
  if (sent) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: '3.5rem', marginBottom: 16, textAlign: 'center' }}>🔑</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, textAlign: 'center' }}>
            Enter Your Code
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', textAlign: 'center', marginBottom: 20, lineHeight: 1.5 }}>
            We sent a 6-digit code to{' '}
            <strong style={{ color: 'var(--text-primary)' }}>{sentToDisplay}</strong>
          </p>

          {/* Error display */}
          {error && (
            <div style={{
              background: 'rgba(211, 71, 74, 0.1)',
              border: '1px solid rgba(211, 71, 74, 0.3)',
              borderRadius: 'var(--radius-sm)',
              padding: '10px 14px',
              marginBottom: 16,
              fontSize: '0.8125rem',
              color: 'var(--accent)',
            }}>
              {error}
            </div>
          )}

          {/* OTP code entry */}
          <form onSubmit={handleVerifyOtp}>
            <div style={{ marginBottom: 16 }}>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                required
                autoFocus
                className="form-input"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  textAlign: 'center',
                  letterSpacing: '0.3em',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <button
              type="submit"
              disabled={verifying || otpCode.length !== 6}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '0.9375rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: 'var(--accent)',
                color: '#fff',
                cursor: (verifying || otpCode.length !== 6) ? 'not-allowed' : 'pointer',
                opacity: (verifying || otpCode.length !== 6) ? 0.6 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {verifying ? 'Verifying...' : 'Verify Code'}
            </button>
          </form>

          {/* Resend code */}
          <button
            onClick={handleResendCode}
            disabled={resendCooldown > 0 || loading}
            style={{
              ...buttonSecondaryStyle,
              marginTop: 16,
              opacity: (resendCooldown > 0 || loading) ? 0.5 : 1,
            }}
          >
            {resendCooldown > 0
              ? `Resend code in ${resendCooldown}s`
              : loading
                ? 'Sending...'
                : 'Resend code'}
          </button>

          {/* Use different method */}
          <button
            onClick={() => {
              setSent(false);
              setIdentifier('');
              setInputType(null);
              setNormalizedPhone('');
              setOtpCode('');
              setError('');
              setResendCooldown(0);
            }}
            style={{
              ...buttonSecondaryStyle,
              marginTop: 8,
            }}
          >
            Try a different method
          </button>

          <div style={{
            marginTop: 16,
            textAlign: 'center',
            padding: '12px 16px',
            background: 'rgba(211, 71, 74, 0.08)',
            border: '1px solid rgba(211, 71, 74, 0.2)',
            borderRadius: 'var(--radius-md, 8px)',
          }}>
            <p style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#fff',
              margin: '0 0 4px',
            }}>
              {inputType === 'phone'
                ? 'Check your text messages for the code.'
                : <>Check your email for a code from{' '}<strong style={{ color: '#D3474A' }}>hello@propermiles.com</strong></>
              }
            </p>
            {inputType !== 'phone' && (
              <p style={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: '#F59E0B',
                margin: 0,
              }}>
                Don&apos;t see it? Check your spam or junk folder.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Club join branding */}
        {joinClub && (
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            {joinClub.logo_url ? (
              <img
                src={joinClub.logo_url}
                alt={joinClub.name}
                style={{ width: 64, height: 64, borderRadius: 'var(--radius-sm)', objectFit: 'cover', margin: '0 auto 12px', display: 'block' }}
              />
            ) : (
              <div style={{
                width: 64, height: 64, borderRadius: 'var(--radius-sm)',
                background: 'var(--accent)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.5rem', fontWeight: 700, margin: '0 auto 12px',
              }}>
                {joinClub.name?.charAt(0)?.toUpperCase()}
              </div>
            )}
            <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
              Join {joinClub.name}
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              Sign in to join this club on Proper Miles
            </p>
          </div>
        )}

        {/* Logo / Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img
            src="/logo-tall.png"
            alt="Proper Miles"
            style={{ height: joinClub ? 48 : 80, objectFit: 'contain', display: 'block', margin: '0 auto 16px' }}
          />
          {!joinClub && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              If you don&apos;t have an account, one will be created for you. Just sign in!
            </p>
          )}
        </div>

        {/* Error display */}
        {error && (
          <div style={{
            background: 'rgba(211, 71, 74, 0.1)',
            border: '1px solid rgba(211, 71, 74, 0.3)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: '0.8125rem',
            color: 'var(--accent)',
          }}>
            {error}
          </div>
        )}

        {/* OAuth buttons — hidden in native app (OAuth providers block WebViews) */}
        {!isNativeApp && (
          <>
            {/* Apple Sign-In */}
            <button
              onClick={handleAppleSignIn}
              disabled={appleLoading}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '0.9375rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: '#000000',
                color: '#ffffff',
                cursor: appleLoading ? 'not-allowed' : 'pointer',
                opacity: appleLoading ? 0.6 : 1,
                transition: 'opacity 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              {/* Apple logo */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              {appleLoading ? 'Connecting...' : 'Sign in with Apple'}
            </button>

            {/* Google Sign-In */}
            <button
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '0.9375rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: '1.5px solid var(--border)',
                background: '#ffffff',
                color: '#3c4043',
                cursor: googleLoading ? 'not-allowed' : 'pointer',
                opacity: googleLoading ? 0.6 : 1,
                transition: 'opacity 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                marginTop: 12,
              }}
            >
              {/* Google "G" logo */}
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.01 24.01 0 000 21.56l7.98-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              {googleLoading ? 'Connecting...' : 'Sign in with Google'}
            </button>

            {/* Facebook Sign-In */}
            <button
              onClick={handleFacebookSignIn}
              disabled={facebookLoading}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '0.9375rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: '#1877F2',
                color: '#ffffff',
                cursor: facebookLoading ? 'not-allowed' : 'pointer',
                opacity: facebookLoading ? 0.6 : 1,
                transition: 'opacity 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                marginTop: 12,
              }}
            >
              {/* Facebook "f" logo */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              {facebookLoading ? 'Connecting...' : 'Sign in with Facebook'}
            </button>

            {/* Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              margin: '24px 0',
            }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Or</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          </>
        )}

        {/* Email / Phone sign-in form */}
        <form onSubmit={handleSubmit}>
          <p style={{
            fontSize: '0.9375rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
            marginBottom: 12,
            textAlign: 'center',
          }}>
            Enter your email or phone number
          </p>
          <div style={{ marginBottom: 16 }}>
            <input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={phoneAuthEnabled ? 'Email or (555) 123-4567' : 'Email address'}
              // Always 'text': type="email" makes iOS silently refuse to submit
              // when someone types a phone number, so the screen never advances.
              type="text"
              inputMode={phoneAuthEnabled ? 'text' : 'email'}
              autoComplete={phoneAuthEnabled ? 'username' : 'email'}
              required
              className="form-input"
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: '0.9375rem',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px 16px',
              fontSize: '0.9375rem',
              fontWeight: 600,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'var(--accent)',
              color: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {loading ? 'Sending...' : 'Send Login Code'}
          </button>
        </form>

        <p style={{
          marginTop: 20,
          textAlign: 'center',
          fontSize: '0.75rem',
          color: 'var(--text-muted)',
        }}>
          No password needed. We{'\u2019'}ll send you a 6-digit code.
        </p>
      </div>
    </div>
  );
}

const containerStyle = {
  minHeight: '100dvh',
  width: '100%',
  background: 'var(--bg-primary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const cardStyle = {
  maxWidth: 400,
  width: '100%',
  background: 'var(--bg-elevated)',
  borderRadius: 'var(--radius-md)',
  padding: 32,
};

const buttonSecondaryStyle = {
  width: '100%',
  padding: '10px 14px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
};
